<script>
  import { format } from 'svelte-i18n';
  import { Button, ButtonGroup, Spinner } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { ImageItem, SearchInput } from '@client/pages/brandkit/components';

  import { addImage, images } from '@client/stores/assets';

  /**
   * @typedef {{
   *   filename: string;
   *   imageurl: string;
   *   metadata?: { imageinfo?: { width?: number; height?: number } };
   * }} ImageAsset
   */

  /** @type {boolean} */
  export let active = false;

  // Size filtering
  /** @type {number} */
  export let minWidth = 200;
  /** @type {number} */
  export let minHeight = 100;

  // Selection mode
  /** @type {boolean} */
  export let selectable = false;
  /** @type {string} */
  export let value = '';
  /** @type {(url: string) => void} */
  export let onSelect = () => {};

  // Drag out mode
  /** @type {boolean} */
  export let draggable = false;
  /** @type {(e: DragEvent, url: string) => void} */
  export let onDragStart = () => {};

  /** @type {HTMLInputElement} */
  let fileInput;
  let isDraggingOver = false;
  let searchQuery = '';
  /** @type {HTMLInputElement} */
  let searchInput;
  /** @type {'all' | 'lg'} */
  let size = 'lg';
  let uploading = false;

  $: if (active && searchInput) searchInput.focus();

  $: filteredImages = $images
    .filter((image) => image.filename.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter((image) => {
      if (size === 'all') return true;
      const { width, height } = image?.metadata?.imageinfo ?? {};
      return width >= minWidth && height >= minHeight;
    })
    .sort((a, b) => a.filename.localeCompare(b.filename));

  /** @param {ImageAsset} image */
  const dimensions = (image) => `${image?.metadata?.imageinfo?.width}x${image?.metadata?.imageinfo?.height}`;

  /** @param {File[]} files */
  const uploadFiles = async (files) => {
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    uploading = true;
    for (const file of imageFiles) {
      await addImage(file);
    }
    uploading = false;
  };

  /** @param {DragEvent} e */
  const handleDragOver = (e) => {
    e.preventDefault();
    isDraggingOver = true;
  };

  /** @param {DragEvent} e */
  const handleDragLeave = (e) => {
    e.preventDefault();
    isDraggingOver = false;
  };

  /** @param {DragEvent} e */
  const handleDrop = async (e) => {
    e.preventDefault();
    isDraggingOver = false;
    await uploadFiles(Array.from(e.dataTransfer?.files ?? []));
  };

  const handleUpload = () => {
    fileInput?.click();
  };

  /** @param {Event} e */
  const handleFileChange = async (e) => {
    const target = /** @type {HTMLInputElement} */ (e.target);
    await uploadFiles(Array.from(target.files ?? []));
    target.value = '';
  };
</script>

<input type="file" accept="image/*" multiple hidden bind:this={fileInput} on:change={handleFileChange} />

<div {...$$restProps}>
  {#if $images?.length}
    <header class="sticky-top bg-white d-flex p-3 pb-2">
      <SearchInput bind:value={searchQuery} bind:inner={searchInput} />
      <ButtonGroup size="sm" class="ms-2">
        <Button active={size === 'lg'} on:click={() => (size = 'lg')}>{$format('label.DISPLAY_SIZE_LARGE')}</Button>
        <Button active={size === 'all'} on:click={() => (size = 'all')}>{$format('label.ALL')}</Button>
      </ButtonGroup>

      <Button size="sm" class="ms-2" disabled={uploading} on:click={handleUpload}>
        {#if uploading}
          <Spinner size="sm" />
        {:else}
          <Icon name="upload" />
        {/if}
      </Button>
    </header>
  {/if}

  <div
    class="list-group list-group-flush drop-zone px-3"
    class:dragging={isDraggingOver}
    on:dragover={handleDragOver}
    on:dragleave={handleDragLeave}
    on:drop={handleDrop}
    role="region"
  >
    {#if isDraggingOver}
      <div class="drop-overlay">
        <span>{$format('label.DROP_IMAGES')}</span>
      </div>
    {/if}

    {#each filteredImages as image}
      <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
      <ImageItem
        active={selectable && value === image.imageurl}
        name={image.filename}
        description={dimensions(image)}
        draggable={draggable ? 'true' : undefined}
        on:click={() => {
          if (selectable) {
            value = image.imageurl;
            onSelect(image.imageurl);
          }
        }}
        on:dragstart={(e) => {
          if (draggable) onDragStart(e, image.imageurl);
        }}
      >
        <img src={image.imageurl} alt={image.filename} loading="lazy" />
      </ImageItem>
    {/each}
  </div>
</div>

<style lang="scss">
  .drop-zone {
    position: relative;
    min-height: 100%;
  }

  .drop-zone.dragging {
    background-color: var(--color-primary-100, #e3f2fd);
  }

  .drop-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(var(--color-primary-rgb, 25, 118, 210), 0.1);
    border: 2px dashed var(--color-primary, #1976d2);
    border-radius: 4px;
    z-index: 10;
    pointer-events: none;

    span {
      padding: 1rem 2rem;
      background: white;
      border-radius: 4px;
      font-weight: 500;
      color: var(--color-primary, #1976d2);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }

  img {
    max-width: 5rem;
  }
</style>
